import './OfflineInputs.scss';

import {
  ControlVariableTable,
  MediaTable,
  MenuItem,
  OfflineInputTabs,
  OutcomeDataTable,
  TimelineEntry
} from '@blisspointmedia/bpm-types/dist/OfflineInputs';
import { useCallback, useEffect, useState } from 'react';
import { Nav } from 'react-bootstrap';
import {
  DEFAULT_OFFERS,
  DEFAULT_PROMOS,
  fetchPromosData,
  fetchTables,
  mergeWithDefaults,
  sortMenuItemsAlphabetically,
  TablesByTab
} from './OfflineInputsUtils';

import { LeaveConfirmationDialog } from './OfflineInputsDialogs';
import { MediaInputs } from './MediaInputs';
import { OutcomeData } from './OutcomeData';
import { Promos } from './Promos';
import { useLocationCompany } from '../../../utils/hooks/useLocationCompany';
import { useSetError } from '../../../redux/modals';
import { ControlVariables } from './ControlVariables';
import { IncrementalityTesting } from './IncrementalityTesting';

const renderTabs = [
  { label: 'Paid Media Inputs', key: OfflineInputTabs.PAID_MEDIA_INPUTS, disabled: false },
  {
    label: 'Non-Paid Media Inputs',
    key: OfflineInputTabs.NON_PAID_MEDIA_INPUTS,
    disabled: false
  },
  { label: 'Control Variables', key: OfflineInputTabs.CONTROL_VARIABLES, disabled: false },
  {
    label: 'Outcome Data',
    key: OfflineInputTabs.OUTCOME_DATA,
    disabled: false
  },
  { label: 'Promos', key: OfflineInputTabs.PROMOS, disabled: false },
  {
    label: 'Incrementality Testing',
    key: OfflineInputTabs.INCREMENTALITY_TESTING,
    disabled: false
  }
];

const defaultTablesByTab = Object.assign(
  {},
  ...Object.values(OfflineInputTabs).map(tab => ({ [tab]: [] }))
);
const defaultUpdatedByTab = Object.assign(
  {},
  ...Object.values(OfflineInputTabs).map(tab => ({ [tab]: { by: undefined, at: undefined } }))
);

export const OfflineInputs = () => {
  const [tablesByTab, setTablesByTab] = useState<TablesByTab>(defaultTablesByTab);
  const [updatedByTab, setUpdatedByTab] =
    useState<Record<OfflineInputTabs, { by?: string; at?: string }>>(defaultUpdatedByTab);

  const [promos, setPromosRaw] = useState<MenuItem[]>([]);
  const [offers, setOffersRaw] = useState<MenuItem[]>([]);
  const [timelineEntries, setTimelineEntries] = useState<TimelineEntry[]>([]);
  const [stagedEntries, setStagedEntries] = useState<TimelineEntry[]>([]);

  const setPromos = useCallback((newPromos: MenuItem[]) => {
    setPromosRaw(sortMenuItemsAlphabetically(mergeWithDefaults(DEFAULT_PROMOS, newPromos)));
  }, []);

  const setOffers = useCallback((newOffers: MenuItem[]) => {
    setOffersRaw(sortMenuItemsAlphabetically(mergeWithDefaults(DEFAULT_OFFERS, newOffers)));
  }, []);

  const [activeTab, setActiveTab] = useState<OfflineInputTabs>(OfflineInputTabs.PAID_MEDIA_INPUTS);
  const [tabToNavTo, setTabToNavTo] = useState<OfflineInputTabs>();
  const [fetching, setFetching] = useState<boolean>(true);
  const [fetchFailed, setFetchFailed] = useState<boolean>(false);
  const [showLeaveConfirmationDialog, setShowLeaveConfirmationDialog] = useState<boolean>(false);
  const [editingUnlocked, setEditingUnlocked] = useState<boolean>(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [dispatchStateReset, setDispatchStateReset] = useState<boolean>(false);
  const company = useLocationCompany() ?? '';
  const setError = useSetError();

  const refetch = async () => {
    setFetching(true);
    const [tablesFetchSucceeded] = await Promise.all([
      fetchTables(company, setTablesByTab, setError, setUpdatedByTab),
      fetchPromosData(company, setPromos, setOffers, setTimelineEntries, setError)
    ]);
    setFetchFailed(!tablesFetchSucceeded);
    setFetching(false);
  };

  useEffect(() => {
    Promise.all([
      fetchTables(company, setTablesByTab, setError, setUpdatedByTab),
      fetchPromosData(company, setPromos, setOffers, setTimelineEntries, setError)
    ]).then(([tablesFetchSucceeded]) => {
      setFetchFailed(!tablesFetchSucceeded);
      setFetching(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  // Props shared by every tab's body component.
  const commonTabProps = {
    fetching,
    fetchFailed,
    refetch,
    editingUnlocked,
    setEditingUnlocked,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    dispatchStateReset,
    setDispatchStateReset,
    company,
    setError
  };

  const renderBody = (tab: OfflineInputTabs) => {
    switch (tab) {
      case OfflineInputTabs.PAID_MEDIA_INPUTS:
        return (
          <MediaInputs
            inputType={OfflineInputTabs.PAID_MEDIA_INPUTS}
            originalTables={tablesByTab[OfflineInputTabs.PAID_MEDIA_INPUTS] as MediaTable[]}
            updated={updatedByTab[OfflineInputTabs.PAID_MEDIA_INPUTS]}
            {...commonTabProps}
          />
        );
      case OfflineInputTabs.NON_PAID_MEDIA_INPUTS:
        return (
          <MediaInputs
            inputType={OfflineInputTabs.NON_PAID_MEDIA_INPUTS}
            originalTables={tablesByTab[OfflineInputTabs.NON_PAID_MEDIA_INPUTS] as MediaTable[]}
            updated={updatedByTab[OfflineInputTabs.NON_PAID_MEDIA_INPUTS]}
            {...commonTabProps}
          />
        );
      case OfflineInputTabs.OUTCOME_DATA:
        return (
          <OutcomeData
            inputType={OfflineInputTabs.OUTCOME_DATA}
            originalTables={tablesByTab[OfflineInputTabs.OUTCOME_DATA] as OutcomeDataTable[]}
            updated={updatedByTab[OfflineInputTabs.OUTCOME_DATA]}
            {...commonTabProps}
          />
        );
      case OfflineInputTabs.PROMOS:
        return (
          <Promos
            inputType={OfflineInputTabs.PROMOS}
            updated={updatedByTab[OfflineInputTabs.PROMOS]}
            promos={promos}
            setPromos={setPromos}
            offers={offers}
            setOffers={setOffers}
            timelineEntries={timelineEntries}
            stagedEntries={stagedEntries}
            setStagedEntries={setStagedEntries}
            {...commonTabProps}
          />
        );
      case OfflineInputTabs.CONTROL_VARIABLES:
        return (
          <ControlVariables
            inputType={OfflineInputTabs.CONTROL_VARIABLES}
            originalTables={
              tablesByTab[OfflineInputTabs.CONTROL_VARIABLES] as ControlVariableTable[]
            }
            updated={updatedByTab[OfflineInputTabs.CONTROL_VARIABLES]}
            {...commonTabProps}
          />
        );
      case OfflineInputTabs.INCREMENTALITY_TESTING:
        return (
          <IncrementalityTesting
            inputType={OfflineInputTabs.INCREMENTALITY_TESTING}
            originalTables={tablesByTab[OfflineInputTabs.INCREMENTALITY_TESTING]}
            updated={updatedByTab[OfflineInputTabs.INCREMENTALITY_TESTING]}
            {...commonTabProps}
          />
        );
      default:
        return <div>{tab} not yet implemented.</div>;
    }
  };

  return (
    <div className='offline-inputs-container'>
      <LeaveConfirmationDialog
        onLeave={() => {
          setDispatchStateReset(true);
          if (tabToNavTo) {
            setActiveTab(tabToNavTo);
          }
        }}
        onStay={() => {
          setTabToNavTo(undefined);
        }}
        show={showLeaveConfirmationDialog}
        onHide={() => setShowLeaveConfirmationDialog(false)}
      />
      <Nav
        activeKey={activeTab}
        onSelect={value => {
          const valueTab = value as keyof typeof value;

          if (!valueTab || valueTab === activeTab) {
            return;
          }
          if (editingUnlocked && hasUnsavedChanges) {
            setTabToNavTo(valueTab);
            setShowLeaveConfirmationDialog(true);
          } else if (editingUnlocked && !hasUnsavedChanges) {
            setDispatchStateReset(true);
            setActiveTab(valueTab);
          } else {
            setActiveTab(valueTab);
          }
        }}
      >
        {renderTabs.map(({ label, key, disabled }) => (
          <Nav.Item key={key}>
            <Nav.Link eventKey={key} disabled={disabled}>
              {label}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      {renderBody(activeTab)}
    </div>
  );
};
